import React from 'react'
import { KITS, type KitId } from '@/hooks/useAudioEngine'

interface KitPickerProps {
  value: KitId
  /** `true` when the current tier allows all kits; otherwise locked to the default. */
  unlockedAll: boolean
  onChange: (kitId: KitId) => void
  onLockedAttempt?: () => void
}

/**
 * Simple kit selector. Plan 1 ships exactly one kit (`classic`), but
 * the shape of this component already supports future kits — the
 * upgrade prompt fires when a locked kit is clicked. Today that's
 * only reachable if future kits get added before the subscription
 * schema does.
 */
export const KitPicker: React.FC<KitPickerProps> = ({
  value,
  unlockedAll,
  onChange,
  onLockedAttempt,
}) => {
  const kitIds = Object.keys(KITS) as KitId[]

  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
        Kit
      </span>
      <div className="flex gap-1">
        {kitIds.map(id => {
          const meta = KITS[id]
          const locked = !unlockedAll && id !== 'classic'
          const active = id === value
          return (
            <button
              key={id}
              type="button"
              onClick={() => {
                if (locked) onLockedAttempt?.()
                else onChange(id)
              }}
              className={[
                // Kit names are chrome (enum-like identifiers like
                // "Nate08", "Warehouse", "Crate", "Prism") — uppercase
                // them for consistency with the tab row + layout-mode
                // buttons. User-entered strings don't flow through here.
                'rounded-md border px-2.5 py-1 text-xs font-mono uppercase tracking-wider transition-colors',
                active
                  ? 'bg-accent-muted border-[color:var(--accent-halo)] text-accent'
                  : locked
                    ? 'border-brd-subtle text-content-tertiary opacity-50'
                    : 'border-brd-subtle text-content-secondary hover:border-brd-emphasis',
              ].join(' ')}
              title={locked ? 'Upgrade to Pro to unlock all kits' : meta.label}
            >
              {locked && '🔒 '}
              {meta.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
